import { useRef, useState } from "react";
import { postFlightRequest } from "apis/loadsServices";
import { useToastStore } from "store/toastStore";
import { useCreditStore } from "store/creditStore";
import { FlightDetails } from "../types";
import { SelectedSegment } from "./useFlightSelection";

interface UsePostRequestArgs {
  selectedFlights: FlightDetails[];
  selectedSegments: SelectedSegment[];
  // Receives optimistic "recently posted" cards built from the selection.
  onPostRequestSuccess?: (optimisticItems?: any[]) => void;
  clearSelection: () => void;
}

// Owns the post-request flow: validation, payload building, the API call,
// optimistic "recently posted" card construction, and credit refresh. Keeps a
// button-level `posting` flag (no global loader).
export function usePostRequest({
  selectedFlights,
  selectedSegments,
  onPostRequestSuccess,
  clearSelection,
}: UsePostRequestArgs) {
  const { showToast } = useToastStore.getState();
  const [posting, setPosting] = useState(false);
  // Synchronous re-entrancy latch: `posting` state only disables the button on
  // the next render, so a fast double-click/tap can call this twice before that.
  // This ref blocks the second call immediately, preventing a duplicate POST.
  const postingRef = useRef(false);

  const handlePostRequest = async () => {
    if (postingRef.current) return;

    if (selectedFlights.length === 0 && selectedSegments.length === 0) {
      showToast(
        "Please select at least one flight to post a request.",
        "error",
        3000,
      );
      return;
    }

    const directFlightRequests = selectedFlights.map((flight) => ({
      airlineId: flight.airlineId,
      flightNumber: flight.flightNumber,
      sourceCode: flight.departureCode,
      destinationCode: flight.arrivalCode,
      departureDate: new Date(`${flight.departureTime}Z`).toISOString(),
      arrvalDate: new Date(`${flight.arrivalTime}Z`).toISOString(),
      depterm: flight.depterminal || "N/A",
      arrterm: flight.arrterminal || "N/A",
      duration: flight.duration || "00:00",
      elptim: flight.elptim || "00:00",
      depcity: flight.depctry || "N/A",
      arrcity: flight.arrctry || "N/A",
    }));

    // Each selected connection segment is posted as a separate request.
    const connectionFlightRequests = selectedSegments.map(({ segment }) => ({
      airlineId: segment.airlineId,
      flightNumber: segment.flightNumber,
      sourceCode: segment.departureCode,
      destinationCode: segment.arrivalCode,
      departureDate: new Date(`${segment.departureTime}Z`).toISOString(),
      arrvalDate: new Date(`${segment.arrivalTime}Z`).toISOString(),
      depterm: segment.depterminal || "N/A",
      arrterm: segment.arrterminal || "N/A",
      duration: segment.duration || "00:00",
      elptim: segment.duration || "00:00",
      depcity: segment.departureAirportName?.split(" ")[0] || "N/A",
      arrcity: segment.arrivalAirportName?.split(" ")[0] || "N/A",
    }));

    const requestData = {
      postRequest: [...directFlightRequests, ...connectionFlightRequests],
    };

    postingRef.current = true;
    setPosting(true);
    try {
      await postFlightRequest(requestData);
      showToast("Flight request posted successfully!", "success", 3000);

      // Optimistic "recently posted" cards (status Pending) so the home list
      // renders them instantly, before the silent backend reconcile.
      const optimisticItems = [
        ...selectedFlights.map((flight, i) => ({
          id: -(i + 1),
          airlineId: flight.airlineId,
          airlineName: flight.airlineName,
          airlineLogo: flight.airlineLogo,
          flightNo: flight.flightNumber,
          sourceCode: flight.departureCode,
          destinationCode: flight.arrivalCode,
          departure: new Date(`${flight.departureTime}Z`).toISOString(),
          arrival: new Date(`${flight.arrivalTime}Z`).toISOString(),
          elptim: flight.elptim || "",
          status: "Pending",
        })),
        ...selectedSegments.map((sel, i) => {
          const seg = sel.segment;
          return {
            id: -(selectedFlights.length + i + 1),
            airlineId: seg.airlineId,
            airlineName: seg.airlineName,
            airlineLogo: seg.airlineLogo,
            flightNo: seg.flightNumber,
            sourceCode: seg.departureCode,
            destinationCode: seg.arrivalCode,
            departure: new Date(`${seg.departureTime}Z`).toISOString(),
            arrival: new Date(`${seg.arrivalTime}Z`).toISOString(),
            elptim: seg.duration || "",
            status: "Pending",
          };
        }),
      ];

      // Hand the posted cards to the home view (returns to the recent list and
      // shows them immediately), then reconcile + refresh tokens in the bg.
      clearSelection();
      onPostRequestSuccess?.(optimisticItems);
      useCreditStore.getState().refresh();
    } catch (error: any) {
      console.error("Failed to post flight request:", error);
      const backendMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to post flight request. Please try again.";
      showToast(backendMessage, "error", 3000);
      // Clear on failure too (e.g. "already requested on the same day") so the
      // highlighted card resets.
      clearSelection();
    } finally {
      setPosting(false);
      postingRef.current = false;
    }
  };

  return { posting, handlePostRequest };
}
